import { useState } from "react";
import { CustomButton } from "@/components/ui/Buttons";

import { Check } from "lucide-react"; // ✅ Professional check icon
import CopyIcon from "@/assets/icons/Copy.svg";
import UploadIcon from "@/assets/icons/UploadWithoutCircle.svg";
import PlayIcon from "@/assets/icons/PlayIcon.svg";

/**
 * Mobile screen component for Assignment task UI
 * Features:
 * - Task details
 * - Script suggestion with copy-to-clipboard functionality
 * - Pro tips
 * - Video requirements
 * - Upload + Sample buttons
 */
const AssignmentMobileScreen = () => {
  const [copied, setCopied] = useState(false);

  // Function to handle copy-to-clipboard
  const handleCopy = () => {
    const scriptText =
      "This is the ___ I’ve been using—here’s why I love it: ___.";
    navigator.clipboard.writeText(scriptText).then(() => {
      setCopied(true);
      // Reset after 1 second
      setTimeout(() => setCopied(false), 1000);
    });
  };

  return (
    <div className="md:hidden border border-[#E4E7EC] rounded-xl py-4 px-3 mx-4 my-6 shadow-sm bg-white">
      {/* Task Section */}
      <div className="space-y-3">
        <h6 className="text-[#ED5E00] text-sm font-medium bg-[#FEF4ED] w-[70px] h-[22px] flex items-center justify-center rounded-full">
          Task
        </h6>

        <h1 className="text-[#101828] text-base font-semibold">
          Show off your UGC skills 📊
        </h1>

        <p className="text-[#8C8C8C] text-sm leading-5">
          Pick any product around you—a snack, shampoo, gadget, whatever—and
          make a short UGC-style video for it. Talk to the camera, show the
          product, or be creative with the hook.
        </p>

        {/* Script Box */}
        <div className="bg-[#F9FAFB] border border-[#E4E7EC] p-3 rounded-lg space-y-3">
          <div className="flex justify-between items-center">
            <h5 className="text-[#101828] font-medium">Steal this script</h5>

            {/* Copy Button */}
            <button
              onClick={handleCopy}
              className="relative bg-white border border-[#E4E7EC] py-1 px-3 rounded-lg flex items-center gap-x-1 text-sm font-medium text-[#686868] hover:bg-[#F3F3F3] transition-colors duration-200"
            >
              {/* Default Copy State */}
              <div
                className={`flex items-center gap-1 transition-all duration-300 ${
                  copied
                    ? "opacity-0 scale-90 absolute"
                    : "opacity-100 scale-100 relative"
                }`}
              >
                <CopyIcon />
                <span>Copy</span>
              </div>

              {/* Copied State */}
              <div
                className={`flex items-center gap-1 text-green-600 transition-all duration-300 ${
                  copied
                    ? "opacity-100 scale-100 relative"
                    : "opacity-0 scale-90 absolute"
                }`}
              >
                <Check size={16} strokeWidth={2.5} />
                <span>Copied!</span>
              </div>
            </button>
          </div>

          <h5 className="text-[#101828] text-sm italic">
            This is the ___ I’ve been using—here’s why I love it: ___.
          </h5>
        </div>
      </div>

      {/* Pro tips + Video Requirements */}
      <div className="space-y-5 mt-5">
        {/* Pro Tips */}
        <div className="bg-[#F7FFF4] border border-[#1D710099] px-2 py-2 rounded-lg space-y-1">
          <h5 className="text-center text-[#1D7100] text-sm font-semibold">
            Pro Tips 🖐
          </h5>
          <p className="text-[#686868] text-sm text-center">
            Hook in first 2s • Face natural light • Crisp audio • Show + Tell •
            Add one clear benefit
          </p>
        </div>

        {/* Divider */}
        <div className="bg-[#E4E7EC] h-[1px]" />

        {/* Requirements */}
        <div className="flex justify-between items-center">
          {["MP4/MOV/WebM", "20–30s", "≤200MB"].map((req, i) => (
            <p
              key={i}
              className="text-[#686868] text-sm font-medium bg-[#F3F3F3] rounded-lg px-2 py-[2px]"
            >
              {req}
            </p>
          ))}
        </div>

        {/* Buttons */}
        <div className="flex justify-between gap-x-3">
          {/* Upload Button */}
          <CustomButton
            color="#FFFFFF"
            fontSize="14px"
            fontWeight="600"
            backgroundColor="#FF6600"
            borderRadius="8px"
            padding="10px 36px"
            className="leading-5 flex gap-x-1 items-center hover:bg-[#e65c00] transition-colors"
          >
            <UploadIcon />
            Upload Video
          </CustomButton>

          {/* Sample Button */}
          <CustomButton
            variant="outline"
            color="#686868"
            fontSize="14px"
            fontWeight="600"
            border="1px solid #E4E7EC"
            borderRadius="8px"
            padding="10px 15px"
            className="leading-5 flex gap-x-1 items-center hover:bg-[#F3F3F3] transition-colors"
          >
            <PlayIcon />
            Sample
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default AssignmentMobileScreen;
